import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Heart, BarChart3, Handshake, BookOpen, ArrowLeft, Globe, Sparkles, Trophy } from 'lucide-react';
import PersonaShowcase from './PersonaShowcase';
import Curriculum from './Curriculum';
import TestPrep from './TestPrep';
import Reports from './Reports';
import HealingWorlds from './HealingWorlds';
import Partnership from './Partnership';
import { useDemoLang } from './demoLang';

const TABS = [
  { key: 'personas',   fr: 'Tuteurs',       en: 'Tutors',       icon: Sparkles,     color: '#0F4C4C' },
  { key: 'curriculum', fr: 'Programme',     en: 'Curriculum',   icon: BookOpen,     color: '#0F4C4C' },
  { key: 'testprep',   fr: 'Prépa Examens', en: 'Test Prep',    icon: Trophy,       color: '#0F4C4C' },
  { key: 'reports',    fr: 'Rapports',      en: 'Reports',      icon: BarChart3,    color: '#0F4C4C' },
  { key: 'healing',    fr: 'Bien-être',     en: 'Wellbeing',    icon: Heart,        color: '#4a8fb8' },
  { key: 'partner',    fr: 'Partenariat',   en: 'Partnership',  icon: Handshake,    color: '#FFCC00' },
];

export default function DemoLayout() {
  const [tab, setTab] = useState('personas');
  const { lang, setLang, t } = useDemoLang();
  const navigate = useNavigate();
  const current = TABS.find((x) => x.key === tab);

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="demo-layout">
      {/* Top bar */}
      <header className="bg-navy text-white px-4 py-3 flex items-center gap-3 shrink-0 shadow-lg">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="demo-back" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gold flex items-center justify-center text-navy font-heading text-lg font-bold shrink-0">O</div>
          <div className="min-w-0">
            <div className="font-heading text-base font-bold leading-none truncate">OuiStuPaStu {t('Démo', 'Demo')}</div>
            <div className="text-[10px] text-white/60 mt-0.5">{t('Aperçu produit — MTN Cameroun', 'Product preview — MTN Cameroon')}</div>
          </div>
        </Link>
        <button onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
          className="ml-auto h-10 px-3 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center gap-1.5 text-sm font-heading font-bold uppercase"
          data-testid="demo-lang-toggle" aria-label="Toggle language">
          <Globe className="w-4 h-4" /> {lang}
        </button>
      </header>

      {/* Tab strip — horizontally scrollable on mobile */}
      <nav className="bg-white border-b-2 border-cream overflow-x-auto shrink-0 shadow-sm" data-testid="demo-tabs">
        <div className="flex min-w-max px-2">
          {TABS.map((tv) => {
            const Icon = tv.icon;
            const active = tab === tv.key;
            return (
              <button key={tv.key} onClick={() => setTab(tv.key)}
                className={`px-4 py-3 flex items-center gap-2 font-heading font-bold text-sm border-b-4 transition-colors whitespace-nowrap ${active ? 'border-gold text-navy' : 'border-transparent text-navy/60 hover:text-navy'}`}
                style={active ? { borderBottomColor: tv.color } : {}}
                data-testid={`demo-tab-${tv.key}`}>
                <Icon className="w-4 h-4" style={{ color: active ? tv.color : undefined }} />
                {t(tv.fr, tv.en)}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="flex-1 overflow-y-auto">
        <div className={`${tab === 'healing' ? 'bg-gradient-to-b from-sky-50 via-emerald-50/50 to-white' : tab === 'partner' ? 'bg-white' : 'bg-cream'} min-h-full`}>
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6">
            {tab === 'personas' && <PersonaShowcase />}
            {tab === 'curriculum' && <Curriculum />}
            {tab === 'testprep' && <TestPrep />}
            {tab === 'reports' && <Reports />}
            {tab === 'healing' && <HealingWorlds />}
            {tab === 'partner' && <Partnership />}
          </div>
        </div>
      </main>
    </div>
  );
}
